import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { AtmService } from '../atm.service';
import { AtmTargetPercentage } from '../models/atm-target-percentage.model';
import { ToasterService } from '../../../services/toaster.service';

export interface JobAtmAddEditDialogData {
    jobId: number;
}

interface AtmForm {
    jobId: FormControl<number>;
    targetMargin: FormControl<number>;
}

@Component({
    selector: 'nova-job-atm-add-edit-dialog',
    templateUrl: './job-atm-add-edit-dialog.component.html',
    styleUrls: ['./job-atm-add-edit-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class JobAtmAddEditDialogComponent implements OnInit {
    atmForm: FormGroup<AtmForm> = new FormGroup<AtmForm>({
        jobId: new FormControl(null, [Validators.required, Validators.min(1)]),
        targetMargin: new FormControl(null, [Validators.required, Validators.min(1), Validators.max(100)])
    });

    existingJobId: number | null;

    private _isSavingAtm = false;

    constructor(
        public dialogRef: MatDialogRef<JobAtmAddEditDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: JobAtmAddEditDialogData,
        private readonly _atmService: AtmService,
        private readonly _toasterService: ToasterService
    ) {}

    get jobId() {
        return this.atmForm.get('jobId');
    }

    get targetMargin() {
        return this.atmForm.get('targetMargin');
    }

    ngOnInit(): void {
        if (this.data.jobId) {
            this.existingJobId = this.data.jobId;
            this.jobId.setValue(this.existingJobId);
            this._atmService
                .getJobAtm(this.existingJobId)
                .pipe(take(1))
                .subscribe((value: AtmTargetPercentage) => {
                    this.targetMargin.patchValue(value?.targetMargin);
                });
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    isSaveDisabled(): boolean {
        return !this.atmForm.valid || this._isSavingAtm;
    }

    saveHandler(): void {
        this._isSavingAtm = true;
        const atmFormValue = this.atmForm.value;

        const request: AtmTargetPercentage = {
            id: atmFormValue.jobId,
            targetMargin: atmFormValue.targetMargin
        };

        this._atmService
            .saveJobTarget(request)
            .pipe(
                take(1),
                catchError(() => of(false))
            )
            .subscribe({
                next: (): void => {
                    this._toasterService.toast(`ATM Saved!`);
                    this.dialogRef.close(true);
                },
                error: () => this._toasterService.toast('Failed to save ATM!'),
                complete: (): boolean => (this._isSavingAtm = false)
            });
    }
}
